import { Loading } from 'components'
import { translate } from 'locales'
import React, { useEffect, useState } from 'react'
import {
  IFinancialMonthDetail,
  IFinancialMonthlyProduction,
  IFinancialSummary,
} from '../types'
import {
  findFinancialMonthHistory,
  findMonthlyProduction,
  findMonthlySummary,
} from '../services'
import {
  getClosingDate,
  getInvoiceDeadline,
  getPaymentMonth,
  getVisibleMonths,
  isMonthClosed,
} from '../utils'
import MonthSummaryCards from './MonthSummaryCards'
import ProductionTimeline from './ProductionTimeline'

interface OverviewProps {
  locale: string
  isPsychology: boolean
}

const emptyDetail = (periodMonth: string): IFinancialMonthDetail => ({
  periodMonth,
  totalValue: 0,
  closingDate: getClosingDate(periodMonth),
  invoiceDeadline: getInvoiceDeadline(periodMonth),
  paymentMonth: getPaymentMonth(periodMonth),
  isClosed: isMonthClosed(periodMonth),
  stats: {
    finished30: 0,
    finished60: 0,
    noShow30: 0,
    noShow60: 0,
    canceledUnder30_30: 0,
    canceledUnder30_60: 0,
  },
})

const Overview = ({ locale, isPsychology }: OverviewProps) => {
  const visibleMonths = getVisibleMonths(new Date().getFullYear())
  const currentMonth = `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1
  ).padStart(2, '0')}`

  const [selectedMonth, setSelectedMonth] = useState(currentMonth)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [productionLoading, setProductionLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [history, setHistory] = useState<IFinancialMonthDetail[]>([])
  const [production, setProduction] = useState<IFinancialMonthlyProduction[]>(
    []
  )
  const [openMonthSummary, setOpenMonthSummary] = useState<IFinancialSummary>({
    totalValue: 0,
    openConsultations: 0,
    closingDate: null,
    invoiceDeadline: null,
    paymentMonth: null,
    stats: {
      finished30: 0,
      finished60: 0,
      noShow30: 0,
      noShow60: 0,
      canceledUnder30_30: 0,
      canceledUnder30_60: 0,
    },
  })

  useEffect(() => {
    findFinancialMonthHistory(
      visibleMonths.length,
      setHistory,
      setHistoryLoading
    )
    findMonthlyProduction(visibleMonths, setProduction, setProductionLoading)
  }, [])

  const settlementDetail = history.find(
    (item) => item.periodMonth === selectedMonth
  )
  const selectedProduction = production.find(
    (item) => item.periodMonth === selectedMonth
  )

  useEffect(() => {
    if (historyLoading || settlementDetail) return
    findMonthlySummary(setOpenMonthSummary, selectedMonth, setSummaryLoading)
  }, [selectedMonth, historyLoading, settlementDetail])

  const selectedDetail =
    settlementDetail ??
    {
      ...emptyDetail(selectedMonth),
      totalValue: openMonthSummary.totalValue,
      stats: openMonthSummary.stats,
    }

  return (
    <>
      {(historyLoading || productionLoading || summaryLoading) && (
        <Loading title={translate('financial.loading')} />
      )}

      <MonthSummaryCards
        detail={selectedDetail}
        status={selectedProduction?.status ?? 'future'}
        locale={locale}
        isPsychology={isPsychology}
      />

      <ProductionTimeline
        months={production}
        selectedMonth={selectedMonth}
        selectedDetail={selectedDetail}
        locale={locale}
        onSelect={setSelectedMonth}
      />
    </>
  )
}

export default Overview
